/**
 * Veldon Edit content schema v1.
 *
 * A site's content is structured data: pages made of ordered sections.
 * Two section kinds exist:
 *  - `pattern`  — typed sections from the pattern catalog (hero, gallery, ...);
 *                 content lives in `fields`, addressed by manifest `path`.
 *  - `freeform` — bespoke HTML + CSS authored by the build agent; editable
 *                 spots are marked with `data-edit="<fieldId>"` attributes
 *                 and described by the manifest.
 *
 * The editable manifest is the contract every surface shares: the inline
 * editor, the content API, and the MCP agent tools all address content
 * exclusively through manifest entries — never by arbitrary DOM paths.
 */
import { z } from 'zod';

/**
 * Slug: lowercase ASCII, digits, single hyphens (canonical anchor form).
 * The one canonical slug schema — import this, never re-declare the regex.
 */
export const Slug = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'must be a lowercase hyphen-separated slug');

const slug = Slug;

/** The 19 pattern types of the catalog. */
export const PatternType = z.enum([
  'hero',
  'features_grid',
  'gallery',
  'footer',
  'header',
  'contact',
  'blog',
  'team',
  'testimonials',
  'pricing',
  'process',
  'cta',
  'faq',
  'hours',
  'menu_card',
  'portfolio',
  'stats_strip',
  'logo_cloud',
  'custom',
]);

/** Field types an editing surface (human or agent) can operate on. */
export const FieldType = z.enum([
  'text',
  'richtext',
  'image',
  'link',
  'list',
  'hours',
  'menu_items',
]);

/**
 * The subset a FREEFORM section can host. Structured collections
 * (list/hours/menu_items) belong to patterns or modules, not to a single
 * data-edit marker in bespoke html — declaring one on freeform is a schema
 * error, not a silent no-op at render time.
 */
export const FreeformFieldType = z.enum(['text', 'richtext', 'image', 'link']);

export const ImageValue = z.object({
  src: z.string().min(1),
  /** Required: images without alt text fail accessibility validation. */
  alt: z.string(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});

export const LinkValue = z.object({
  href: z.string().min(1),
  text: z.string().min(1),
});

/** JSON value for pattern `fields` — mirrors what patterns accept as kwargs. */
export const JsonValue: z.ZodType<JsonValueT> = z.lazy(() =>
  z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(JsonValue), z.record(z.string(), JsonValue)]),
);
export type JsonValueT = string | number | boolean | null | JsonValueT[] | { [key: string]: JsonValueT };

export const ManifestConstraints = z.object({
  maxLength: z.number().int().positive().optional(),
  minItems: z.number().int().nonnegative().optional(),
  maxItems: z.number().int().positive().optional(),
});

const manifestBase = {
  /** Stable identifier of the editable field, unique within the section. */
  fieldId: slug,
  type: FieldType,
  /** Human label shown in the editor, in the site's locale. */
  label: z.string().min(1).max(120),
  constraints: ManifestConstraints.optional(),
};

/** Manifest entry for pattern sections: addresses a spot in `fields`. */
export const PatternManifestEntry = z.object({
  ...manifestBase,
  /** JSON-pointer-style path into the section's `fields` (e.g. "/heading", "/items/0/title"). */
  path: z.string().regex(/^(?:\/[^/]+)+$/, 'must be a JSON pointer like /heading or /items/0/title'),
});

/** Manifest entry for freeform sections: addresses a `data-edit` marker in `html`. */
export const FreeformManifestEntry = z.object({ ...manifestBase, type: FreeformFieldType });

const sectionBase = {
  /** Unique section id within the page. */
  id: slug,
  /** Canonical in-page anchor. */
  anchor: slug,
};

export const PatternSection = z.object({
  ...sectionBase,
  kind: z.literal('pattern'),
  type: PatternType,
  variant: z.string().min(1).max(60),
  fields: z.record(z.string(), JsonValue),
  editableManifest: z.array(PatternManifestEntry),
});

export const FreeformSection = z.object({
  ...sectionBase,
  kind: z.literal('freeform'),
  /** Complete section markup. Editable spots carry data-edit="<fieldId>". */
  html: z.string().min(1),
  /** Section-scoped CSS, emitted into the page's stylesheet. */
  css: z.string().optional(),
  editableManifest: z.array(FreeformManifestEntry),
});

export const Section = z.discriminatedUnion('kind', [PatternSection, FreeformSection]);

export const Page = z.object({
  id: slug,
  /** URL path, "/" for the homepage. */
  path: z.string().regex(/^\/(?:[a-z0-9-]+(?:\/[a-z0-9-]+)*)?$/, 'must be a rooted lowercase path'),
  title: z.string().min(1).max(200),
  metaDescription: z.string().max(320).optional(),
  sections: z.array(Section).min(1),
});

export const NavItem = z.object({
  label: z.string().min(1).max(80),
  /** "#anchor" for in-page targets, "/path" for pages, or an absolute URL. */
  target: z.string().min(1),
});

export const SiteMeta = z.object({
  name: z.string().min(1).max(120),
  /** BCP-47 primary language subtag (cs, en, pl, ...). */
  locale: z.string().regex(/^[a-z]{2}(?:-[A-Z]{2})?$/),
  domain: z.string().max(253).optional(),
  tagline: z.string().max(200).optional(),
});

export const SiteContent = z.object({
  schemaVersion: z.literal(1),
  site: SiteMeta,
  nav: z.object({ items: z.array(NavItem) }),
  pages: z.array(Page).min(1),
});

export type PatternTypeT = z.infer<typeof PatternType>;
export type FieldTypeT = z.infer<typeof FieldType>;
export type ImageValueT = z.infer<typeof ImageValue>;
export type LinkValueT = z.infer<typeof LinkValue>;
export type PatternManifestEntryT = z.infer<typeof PatternManifestEntry>;
export type FreeformManifestEntryT = z.infer<typeof FreeformManifestEntry>;
export type PatternSectionT = z.infer<typeof PatternSection>;
export type FreeformSectionT = z.infer<typeof FreeformSection>;
export type SectionT = z.infer<typeof Section>;
export type PageT = z.infer<typeof Page>;
export type SiteContentT = z.infer<typeof SiteContent>;

/** JSON Schema of the whole content document (for capability discovery + MCP tools). */
export function siteContentJsonSchema(): Record<string, unknown> {
  return z.toJSONSchema(SiteContent) as Record<string, unknown>;
}
